/**
 * Modal completo de análise de recurso (isenção, lista, resultado preliminar).
 * Mesmo fluxo para analista e admin: narrativa, documentos, notas, análise curricular na lista, decisão.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../ui/Alert';
import { Modal } from '../ui/Modal';
import { GradeInput } from '../ui/GradeInput';
import { apiClient } from '../../api/apiClient';
import { getAppealTypeLabel } from '../../constants/displayLabels';
import {
  appealTypeToRejectionContext,
  filterRejectionReasonsByContext,
} from '../../constants/rejectionReasonContexts';
import { isOrdemInscricaoMode } from '../../utils/selectionMode';

function buildMemoryOfCalculation(grades, courseSubjects) {
  if (!grades || !courseSubjects?.length) return [];
  const fmt = (n) => (n != null && !isNaN(n) ? Number(n).toFixed(2).replace('.', ',') : '—');
  const steps = [];
  const serieResults = [];
  courseSubjects.forEach((subject) => {
    const serieGrades = grades[subject.serie];
    if (!serieGrades || !subject.disciplinas?.length) return;
    const termos = [];
    const pesos = [];
    const produtos = [];
    let serieWeightedSum = 0;
    let serieTotalWeight = 0;
    subject.disciplinas.forEach((d) => {
      const g = serieGrades[d.name];
      if (g !== undefined && g !== null && g >= 0) {
        const prod = g * d.weight;
        serieWeightedSum += prod;
        serieTotalWeight += d.weight;
        termos.push(`${fmt(g)}×${d.weight}`);
        pesos.push(d.weight);
        produtos.push(prod);
      }
    });
    if (serieTotalWeight > 0) {
      const mediaSerie = serieWeightedSum / serieTotalWeight;
      serieResults.push(mediaSerie);
      const somaPesos = pesos.join(' + ');
      steps.push({
        serie: subject.serie || `Série ${steps.length + 1}`,
        formula: `(${termos.join(' + ')}) / (${somaPesos})`,
        intermediario: `(${produtos.map((p) => fmt(p)).join(' + ')}) / ${serieTotalWeight}`,
        resultado: `${fmt(serieWeightedSum)} / ${serieTotalWeight} = ${fmt(mediaSerie)}`,
      });
    }
  });
  if (serieResults.length > 0) {
    const mediaFinal = serieResults.reduce((a, b) => a + b, 0) / serieResults.length;
    const mediaFinalArred = Math.round(mediaFinal * 100) / 100;
    steps.push({
      serie: 'Média final',
      formula: `(${serieResults.map((r) => fmt(r)).join(' + ')}) / ${serieResults.length}`,
      intermediario: null,
      resultado: `${fmt(serieResults.reduce((a, b) => a + b, 0))} / ${serieResults.length} = ${fmt(mediaFinalArred)}`,
    });
  }
  return steps;
}

function calculateWeightedAverage(grades, courseSubjects) {
  if (!grades || !courseSubjects) return 0;
  let totalSeriesAverage = 0;
  let seriesCount = 0;
  courseSubjects.forEach((subject) => {
    const serieGrades = grades[subject.serie];
    if (serieGrades) {
      let serieWeightedSum = 0;
      let serieTotalWeight = 0;
      subject.disciplinas.forEach((d) => {
        const g = serieGrades[d.name];
        if (g !== undefined && g !== null && g !== '' && !isNaN(parseFloat(g))) {
          serieWeightedSum += parseFloat(g) * d.weight;
          serieTotalWeight += d.weight;
        }
      });
      if (serieTotalWeight > 0) {
        totalSeriesAverage += serieWeightedSum / serieTotalWeight;
        seriesCount++;
      }
    }
  });
  return seriesCount > 0 ? Math.round((totalSeriesAverage / seriesCount) * 100) / 100 : 0;
}

function validateAllGrades(grades, courseSubjects) {
  if (!courseSubjects || !grades) return { isValid: false, missingGrades: [] };
  const missing = [];
  courseSubjects.forEach((s) => {
    (s.disciplinas || []).forEach((d) => {
      const v = grades[s.serie]?.[d.name];
      if (v === undefined || v === null || v === '' || (typeof v === 'number' && isNaN(v))) missing.push(`${s.serie} - ${d.name}`);
    });
  });
  return { isValid: missing.length === 0, missingGrades: missing };
}

export function AppealAnalysisModal({
  isOpen,
  onClose,
  resource,
  user,
  rejectionReasons = [],
  addNotification,
  onSaved,
}) {
  const [resourceForm, setResourceForm] = useState({ decision: '', notes: '', rejection_reason: '' });
  const [curriculumForm, setCurriculumForm] = useState({ grades: {}, status: 'approved' });
  const [applicationForCurricular, setApplicationForCurricular] = useState(null);
  const [showGradeDetails, setShowGradeDetails] = useState(false);
  const [examAppealScores, setExamAppealScores] = useState({});
  const [examAppealEssayScore, setExamAppealEssayScore] = useState('');
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState('');
  const [answerKeyQuestionHint, setAnswerKeyQuestionHint] = useState(null);
  const [answerKeyHintLoading, setAnswerKeyHintLoading] = useState(false);
  const [answerKeyHintError, setAnswerKeyHintError] = useState('');

  const selectedResource = resource;

  const resourceReasonContext = useMemo(
    () => appealTypeToRejectionContext(resource?.resource_type || resource?.appeal_type),
    [resource?.resource_type, resource?.appeal_type],
  );

  const filteredResourceReasons = useMemo(
    () => filterRejectionReasonsByContext(rejectionReasons, resourceReasonContext),
    [rejectionReasons, resourceReasonContext],
  );

  useEffect(() => {
    if (!isOpen || !resource?.process_id) {
      if (!isOpen) setCourses([]);
      return;
    }
    let cancelled = false;
    apiClient.getCourses(resource.process_id).then((data) => {
      if (!cancelled) setCourses(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [isOpen, resource?.process_id]);

  useEffect(() => {
    if (!isOpen || !resource) return;
    setApplicationForCurricular(null);
    setCurriculumForm({ grades: {}, status: 'approved' });
    setShowGradeDetails(false);
    setExamAppealScores({});
    setExamAppealEssayScore('');
    setError('');
    const isEdit = resource.status === 'approved' || resource.status === 'rejected';
    let decision = '';
    let notes = '';
    let rejection_reason = '';
    if (isEdit) {
      decision = resource.status;
      const an = (resource.analyst_notes || '').trim();
      if (an.startsWith('Motivo: ')) {
        const parts = an.split(/\n\n+/);
        const motivoTitle = parts[0].replace(/^Motivo:\s*/, '').trim();
        const reason = rejectionReasons.find((r) => (r.title || '').trim() === motivoTitle);
        rejection_reason = reason ? String(reason.id) : '';
        notes = parts.slice(1).join('\n\n').trim();
        if (!notes && !rejection_reason) notes = parts[0];
      } else {
        notes = an;
      }
    }
    setResourceForm({ decision, notes, rejection_reason });
    setExamAppealScores({});
    setExamAppealEssayScore('');
    setAnswerKeyQuestionHint(null);
    setAnswerKeyHintError('');
    setAnswerKeyHintLoading(false);
  }, [isOpen, resource?.id, resource?.status, resource?.analyst_notes, rejectionReasons]);

  const parseDecimalInput = useCallback((raw) => {
    const normalized = String(raw ?? '').trim().replace(/\s/g, '').replace(',', '.');
    if (normalized === '') return null;
    const n = parseFloat(normalized);
    return Number.isNaN(n) ? NaN : n;
  }, []);

  const reportSaveError = useCallback((message) => {
    setError(message);
    requestAnimationFrame(() => {
      document.getElementById('appeal-analysis-save-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const isAnswerKeyAppeal = useCallback(() => {
    const t = (resource?.resource_type || resource?.appeal_type || '').toLowerCase();
    return t === 'answer_key';
  }, [resource]);

  useEffect(() => {
    if (!isOpen || !resource?.id || !isAnswerKeyAppeal() || !resource.question_number) {
      return undefined;
    }
    let cancelled = false;
    setAnswerKeyHintLoading(true);
    setAnswerKeyHintError('');
    setAnswerKeyQuestionHint(null);
    apiClient.getAppealAnswerKeyQuestionHint(resource.id).then(({ data, error: hintErr }) => {
      if (cancelled) return;
      setAnswerKeyHintLoading(false);
      if (hintErr) {
        setAnswerKeyHintError(hintErr);
        return;
      }
      setAnswerKeyQuestionHint(data || null);
    });
    return () => { cancelled = true; };
  }, [isOpen, resource?.id, resource?.question_number, isAnswerKeyAppeal]);

  const getResourceTypeLabel = useCallback((type) => {
    if (!type) return '—';
    return getAppealTypeLabel(type);
  }, []);

  const isListAppeal = useCallback(() => {
    const t = (selectedResource?.resource_type || selectedResource?.appeal_type || '').toLowerCase();
    return t === 'list' || t === 'enrolled_list' || t === 'registration_list';
  }, [selectedResource]);

  const handleDownloadDocuments = useCallback(async (res, index) => {
    const doc = res.documents?.[index] || {};
    const name = doc.name || `documento_${index + 1}.pdf`;
    const docUrl = doc.url;
    if (docUrl && (docUrl.startsWith('/static/') || docUrl.startsWith('http'))) {
      const a = document.createElement('a');
      a.href = docUrl.startsWith('/') ? `${window.location.origin}${docUrl}` : docUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      addNotification('Download', 'Download iniciado', 'success');
      return;
    }
    try {
      const blob = await apiClient.downloadDocument({ appealId: res.id, index });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addNotification('Download', 'Documento baixado com sucesso', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao baixar documento', 'error');
    }
  }, [addNotification]);

  const handleGradeChange = useCallback((serie, disciplina, value) => {
    setCurriculumForm((prev) => ({
      ...prev,
      grades: {
        ...prev.grades,
        [serie]: { ...prev.grades[serie], [disciplina]: value },
      },
    }));
  }, []);

  const handleClose = useCallback(() => {
    setApplicationForCurricular(null);
    setShowGradeDetails(false);
    setError('');
    onClose();
  }, [onClose]);

  const openCurricularForm = useCallback(async () => {
    if (!selectedResource?.application_id) return;
    try {
      const app = await apiClient.getApplication(selectedResource.application_id);
      setApplicationForCurricular(app);
      const courseId = parseInt(selectedResource.course_id || app?.course_id, 10);
      const course = courseId ? (Array.isArray(courses) ? courses : []).find((c) => c.id === courseId) : null;
      let initialGrades = {};
      if (course?.subjects) {
        course.subjects.forEach((s) => {
          initialGrades[s.serie] = {};
          (s.disciplinas || []).forEach((d) => { initialGrades[s.serie][d.name] = 0; });
        });
      }
      const saved = app?.analysis_grades;
      if (saved && typeof saved === 'object') {
        Object.keys(saved).forEach((serie) => {
          if (initialGrades[serie]) {
            Object.keys(saved[serie] || {}).forEach((d) => {
              if (initialGrades[serie][d] !== undefined) initialGrades[serie][d] = saved[serie][d];
            });
          }
        });
      }
      setCurriculumForm({ grades: initialGrades, status: 'approved' });
      setResourceForm((prev) => ({ ...prev, decision: 'approved' }));
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao carregar inscrição', 'error');
    }
  }, [selectedResource, courses, addNotification]);

  const saveListAppealCurricular = useCallback(async () => {
    if (!selectedResource || !applicationForCurricular || !user?.id) return;
    const courseId = parseInt(applicationForCurricular.course_id || selectedResource.course_id, 10);
    const course = courses.find((c) => c.id === courseId);
    if (!course?.subjects) {
      addNotification('Erro', 'Curso ou disciplinas não configuradas', 'error');
      return;
    }
    const validation = validateAllGrades(curriculumForm.grades, course.subjects);
    if (!validation.isValid) {
      addNotification('Erro', `Preencha todas as notas. Faltando: ${validation.missingGrades.join(', ')}`, 'error');
      return;
    }
    const finalAverage = calculateWeightedAverage(curriculumForm.grades, course.subjects);
    try {
      const { error: analysisError } = await apiClient.createAnalysis({
        application_id: selectedResource.application_id,
        status: curriculumForm.status,
        final_score: finalAverage,
        grades: curriculumForm.grades,
      });
      if (analysisError) throw new Error(analysisError);
      const { error: updateError } = await apiClient.updateApplication(selectedResource.application_id, {
        analysis_status: curriculumForm.status,
        analyst_id: user.id,
        analysis_type: 'appeal_list',
        ...(curriculumForm.status === 'rejected' && curriculumForm.rejection_reason ? { rejection_reason: curriculumForm.rejection_reason } : {}),
      });
      if (updateError) throw new Error(updateError);
      const { error: appealError } = await apiClient.updateAppeal(selectedResource.id, {
        status: 'approved',
        analyst_id: user.id,
        analyst_notes: resourceForm.notes || `Análise curricular via recurso. Média: ${finalAverage.toFixed(2)}`,
        final_score: finalAverage,
        grades: curriculumForm.grades,
      });
      if (appealError) throw new Error(appealError);
      addNotification('✅ Recurso deferido', `Inscrição aprovada. Média: ${finalAverage.toFixed(2)}`, 'success');
      handleClose();
      if (onSaved) await onSaved();
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao salvar análise', 'error');
    }
  }, [selectedResource, applicationForCurricular, curriculumForm, courses, user, resourceForm, addNotification, onSaved, handleClose]);

  const saveResourceAnalysis = useCallback(async () => {
    if (!selectedResource || saving) return;
    setError('');
    if (!resourceForm.decision) {
      reportSaveError('É obrigatório selecionar uma decisão');
      return;
    }
    if (resourceForm.decision !== 'approved' && resourceForm.decision !== 'rejected') {
      reportSaveError('Selecione Aprovado ou Rejeitado.');
      return;
    }
    const listApp = isListAppeal();
    const resultApp = (selectedResource?.resource_type || selectedResource?.appeal_type || '').toLowerCase() === 'preliminary_result';
    const answerKeyApp = (selectedResource?.resource_type || selectedResource?.appeal_type || '').toLowerCase() === 'answer_key';
    const specialAttendanceApp = (selectedResource?.resource_type || selectedResource?.appeal_type || '').toLowerCase() === 'special_attendance';
    const isProva = (selectedResource?.process_selection_mode || '') === 'prova';
    const processUsesEssay = Boolean(
      selectedResource?.exam_uses_essay
      ?? (selectedResource?.exam_combined_final_score != null && selectedResource?.exam_essay_score != null),
    );
    const ordemListMode = listApp && isOrdemInscricaoMode(selectedResource?.process_selection_mode);
    if (listApp && resourceForm.decision === 'approved' && !ordemListMode) {
      await openCurricularForm();
      return;
    }
    if (
      resourceForm.decision === 'rejected'
      && !answerKeyApp
      && !resourceForm.rejection_reason
      && !resourceForm.notes
    ) {
      reportSaveError('É obrigatório informar o motivo (selecione um motivo ou preencha as observações) quando rejeitar o recurso');
      return;
    }
    if (resourceForm.decision === 'rejected' && answerKeyApp && !(resourceForm.notes || '').trim()) {
      reportSaveError('Informe a fundamentação da decisão ao indeferir o recurso');
      return;
    }
    if (!user?.id) {
      reportSaveError('Sessão inválida. Recarregue a página e tente novamente.');
      return;
    }
    setSaving(true);
    try {
      const analystNotes = answerKeyApp
        ? (resourceForm.notes || '').trim() || null
        : [
          resourceForm.rejection_reason ? `Motivo: ${rejectionReasons.find((r) => r.id == resourceForm.rejection_reason)?.title || resourceForm.rejection_reason}` : '',
          resourceForm.notes || '',
        ].filter(Boolean).join('\n\n');
      const appealPayload = {
        status: resourceForm.decision === 'approved' ? 'approved' : 'rejected',
        analyst_id: user.id,
        analyst_notes: analystNotes || resourceForm.notes || null,
      };
      if (resultApp && isProva && resourceForm.decision === 'approved') {
        const weights = Array.isArray(selectedResource.course_exam_area_weights)
          ? selectedResource.course_exam_area_weights
          : [];
        const parsed = {};
        let anyObjectiveFilled = false;
        for (const r of weights) {
          const area = (r.area || '').trim();
          if (!area) continue;
          const raw = (examAppealScores[area] ?? '').toString().trim();
          if (raw === '') continue;
          anyObjectiveFilled = true;
          const n = parseDecimalInput(raw);
          if (Number.isNaN(n)) {
            reportSaveError(`Nota inválida na área "${area}"`);
            return;
          }
          parsed[area] = n;
        }
        if (anyObjectiveFilled) {
          const missingArea = weights.find((r) => {
            const area = (r.area || '').trim();
            return area && parsed[area] === undefined;
          });
          if (missingArea) {
            reportSaveError('Ao revisar notas objetivas, preencha todas as áreas ou deixe todas em branco.');
            return;
          }
          appealPayload.exam_area_scores = parsed;
        }
        if (processUsesEssay) {
          const essayDraft = (examAppealEssayScore ?? '').trim();
          if (essayDraft !== '') {
            const essayNum = parseDecimalInput(essayDraft);
            if (essayNum == null || Number.isNaN(essayNum)) {
              reportSaveError('Nota da redação inválida.');
              return;
            }
            const essayMax = Math.max(1, Math.min(500, parseInt(String(selectedResource.exam_essay_max_score ?? 40), 10) || 40));
            if (essayNum < 0 || essayNum > essayMax) {
              reportSaveError(`Nota da redação deve estar entre 0 e ${essayMax}.`);
              return;
            }
            appealPayload.exam_essay_score = essayNum;
          }
        }
      }
      const { error: appealError } = await apiClient.updateAppeal(selectedResource.id, appealPayload);
      if (appealError) throw new Error(appealError);
      if (ordemListMode && resourceForm.decision === 'approved') {
        const { error: appErr } = await apiClient.updateApplication(selectedResource.application_id, {
          analysis_status: 'approved',
          analyst_id: user.id,
          analysis_type: 'appeal_list',
        });
        if (appErr) throw new Error(appErr);
      }
      if (!listApp && !resultApp && !specialAttendanceApp && !answerKeyApp) {
        await apiClient.updateApplication(selectedResource.application_id, {
          exemption_status: resourceForm.decision === 'approved' ? 'appeal_approved' : 'appeal_rejected',
        });
      }
      const decisionText = resourceForm.decision === 'approved' ? 'Aprovado' : 'Rejeitado';
      addNotification('✅ Recurso analisado', `Recurso ${decisionText} com sucesso`, 'success');
      handleClose();
      if (onSaved) await onSaved();
    } catch (err) {
      console.error(err);
      reportSaveError(err.message || 'Erro ao salvar análise do recurso');
      addNotification('❌ Erro', err.message || 'Erro ao salvar análise do recurso', 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedResource, resourceForm, examAppealScores, examAppealEssayScore, saving, user, parseDecimalInput, reportSaveError, addNotification, onSaved, isListAppeal, openCurricularForm, rejectionReasons, handleClose]);

  const appealTypeKey = selectedResource?.appeal_type || selectedResource?.resource_type;
  const isProvaResource = (selectedResource?.process_selection_mode || '') === 'prova';
  const isOrdemResource = isOrdemInscricaoMode(selectedResource?.process_selection_mode);
  const examWeightRows = useMemo(
    () => (Array.isArray(selectedResource?.course_exam_area_weights) ? selectedResource.course_exam_area_weights : []).filter((r) => (r?.area || '').trim()),
    [selectedResource?.course_exam_area_weights],
  );
  const usesExamEssay = Boolean(selectedResource?.exam_uses_essay);
  const essayMaxScore = useMemo(() => {
    const n = parseInt(String(selectedResource?.exam_essay_max_score ?? 40), 10);
    if (Number.isNaN(n)) return 40;
    return Math.max(1, Math.min(500, n));
  }, [selectedResource?.exam_essay_max_score]);
  const fmtExamScore = (value) => (value != null && !Number.isNaN(Number(value))
    ? Number(value).toFixed(2).replace('.', ',')
    : '—');
  const showProvaGradeRevision = appealTypeKey === 'preliminary_result'
    && resourceForm.decision === 'approved'
    && (examWeightRows.length > 0 || usesExamEssay);
  const areaScoresCurrent = selectedResource?.exam_area_scores && typeof selectedResource.exam_area_scores === 'object'
    ? selectedResource.exam_area_scores
    : {};
  const memorySteps = useMemo(() => {
    if (!selectedResource?.analysis_grades || !selectedResource?.course_subjects?.length) return [];
    return buildMemoryOfCalculation(selectedResource.analysis_grades, selectedResource.course_subjects);
  }, [selectedResource?.analysis_grades, selectedResource?.course_subjects]);

  if (!resource) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Análise de Recurso - ${resource.candidate_name}`}
      large
    >
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>👤 Informações do Candidato</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><strong>Nome:</strong> {resource.candidate_name}</div>
            <div><strong>CPF:</strong> {resource.candidate_cpf}</div>
            <div>
              <strong>Curso:</strong> {resource.course_name}<br />
              <strong>Unidade:</strong> {resource.course_campus || '—'}
            </div>
            <div><strong>Tipo:</strong> {getResourceTypeLabel(resource.resource_type || resource.appeal_type)}</div>
          </div>
        </div>

        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: (resource.current_situation_label || '').toLowerCase().includes('indeferido') ? '#FEE2E2' : (resource.current_situation_label || '').toLowerCase().includes('deferido') ? '#D1FAE5' : '#F3F4F6',
          borderRadius: '8px',
          border: (resource.current_situation_label || '').toLowerCase().includes('indeferido') ? '1px solid #FCA5A5' : (resource.current_situation_label || '').toLowerCase().includes('deferido') ? '1px solid #6EE7B7' : '1px solid #E5E7EB',
        }}>
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            color: (resource.current_situation_label || '').toLowerCase().includes('indeferido') ? '#991B1B' : (resource.current_situation_label || '').toLowerCase().includes('deferido') ? '#065F46' : '#374151',
          }}>
            {(resource.current_situation_label || '').toLowerCase().includes('indeferido') ? '❌' : (resource.current_situation_label || '').toLowerCase().includes('deferido') ? '✅' : '📋'} Situação atual: {resource.current_situation_label || '—'}
          </h4>
          {resource.current_reason_title && (
            <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
              <strong>Motivo:</strong> {resource.current_reason_title}
            </p>
          )}
        </div>

        {isProvaResource && ['preliminary_result', 'answer_key'].includes(appealTypeKey) && (
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>
              {appealTypeKey === 'preliminary_result' && usesExamEssay
                ? 'Notas da prova (objetiva e redação)'
                : 'Notas da prova (objetiva)'}
            </h4>
            {(resource.exam_final_score != null
              || (appealTypeKey === 'preliminary_result' && usesExamEssay)
              || resource.exam_combined_final_score != null) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '10px',
                  marginBottom: showProvaGradeRevision ? '16px' : 0,
                }}
              >
                {resource.exam_final_score != null && (
                  <div style={{ padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>Objetiva</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{fmtExamScore(resource.exam_final_score)}</div>
                  </div>
                )}
                {appealTypeKey === 'preliminary_result' && usesExamEssay && (
                  <div style={{ padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>Redação</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{fmtExamScore(resource.exam_essay_score)}</div>
                  </div>
                )}
                {appealTypeKey === 'preliminary_result' && usesExamEssay && resource.exam_combined_final_score != null && (
                  <div style={{ padding: '10px 12px', background: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                    <div style={{ fontSize: '11px', color: '#1D4ED8', marginBottom: '4px' }}>Total</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#1E3A8A' }}>{fmtExamScore(resource.exam_combined_final_score)}</div>
                  </div>
                )}
              </div>
            )}
            {showProvaGradeRevision && (
              <div
                style={{
                  marginTop: '4px',
                  padding: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  borderLeft: '4px solid #2563EB',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937', marginBottom: '4px' }}>
                  Revisão de notas (opcional)
                </div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 16px 0', lineHeight: 1.45 }}>
                  Preencha somente se o deferimento exigir alteração. Objetiva: informe todas as áreas ou nenhuma.
                  Redação: altere a nota abaixo se necessário. O total e o corte são recalculados ao salvar.
                </p>
                {examWeightRows.length > 0 && (
                  <div style={{ marginBottom: usesExamEssay ? '20px' : 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Prova objetiva</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#F9FAFB' }}>
                            <th style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid #E5E7EB' }}>Área</th>
                            <th style={{ textAlign: 'center', padding: '8px 10px', border: '1px solid #E5E7EB', width: 72 }}>Peso</th>
                            <th style={{ textAlign: 'center', padding: '8px 10px', border: '1px solid #E5E7EB', width: 96 }}>Atual</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid #E5E7EB', width: 120 }}>Nova nota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {examWeightRows.map((r) => {
                            const area = (r.area || '').trim();
                            const current = areaScoresCurrent[area];
                            return (
                              <tr key={area}>
                                <td style={{ padding: '8px 10px', border: '1px solid #E5E7EB', fontWeight: 500 }}>{area}</td>
                                <td style={{ padding: '8px 10px', border: '1px solid #E5E7EB', textAlign: 'center', color: '#6B7280' }}>{r.weight}</td>
                                <td style={{ padding: '8px 10px', border: '1px solid #E5E7EB', textAlign: 'center' }}>
                                  {current != null && current !== '' ? fmtExamScore(current) : '—'}
                                </td>
                                <td style={{ padding: '6px 8px', border: '1px solid #E5E7EB' }}>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={examAppealScores[area] ?? ''}
                                    onChange={(e) => setExamAppealScores((s) => ({ ...s, [area]: e.target.value }))}
                                    placeholder="—"
                                    style={{ ...styles.input, width: '100%', padding: '6px 8px', fontSize: '13px' }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {usesExamEssay && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Redação</div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px 16px',
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <label style={{ fontSize: '13px', color: '#374151', minWidth: '140px' }}>
                        Nova nota <span style={{ color: '#6B7280' }}>(0–{essayMaxScore})</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={examAppealEssayScore}
                        onChange={(e) => setExamAppealEssayScore(e.target.value)}
                        placeholder="Informe a nova nota"
                        style={{ ...styles.input, width: '100px', padding: '8px 10px', fontSize: '14px', fontWeight: 600 }}
                      />
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        Atual: <strong>{fmtExamScore(resource.exam_essay_score)}</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {appealTypeKey === 'answer_key' && (
              <>
                {resource.question_number && (
                  <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                    <strong>Questão do recurso:</strong> {resource.question_number}
                  </p>
                )}
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#1E3A8A',
                    lineHeight: 1.5,
                  }}
                >
                  {answerKeyHintLoading && 'Carregando resposta do gabarito preliminar…'}
                  {!answerKeyHintLoading && answerKeyHintError && (
                    <span style={{ color: '#B45309' }}>{answerKeyHintError}</span>
                  )}
                  {!answerKeyHintLoading && !answerKeyHintError && answerKeyQuestionHint && (
                    <>
                      <strong>Gabarito preliminar — questão {answerKeyQuestionHint.question_number}</strong>
                      <div style={{ marginTop: '6px' }}>
                        Tipo de folha: <strong>{answerKeyQuestionHint.variant_label}</strong>
                        {answerKeyQuestionHint.variant_uncertain && (
                          <span style={{ color: '#6B7280' }}>
                            {' '}
                            (confira o tipo impresso na folha de respostas do candidato)
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: '6px' }}>
                        Resposta oficial: <strong>{answerKeyQuestionHint.answer_display}</strong>
                      </div>
                    </>
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                  A decisão registra-se aqui; correções no gabarito oficial são feitas em Processos → Gabarito.
                </p>
              </>
            )}
          </div>
        )}

        {['preliminary_result', 'list'].includes(appealTypeKey) && !isProvaResource && (resource.analysis_grades || resource.final_score != null) && (
          <div style={{ marginBottom: '24px' }}>
            <button
              type="button"
              onClick={() => setShowGradeDetails(!showGradeDetails)}
              style={{ ...styles.button, ...styles.buttonSecondary, display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              📊 {showGradeDetails ? 'Ocultar' : 'Ver'} notas e memória de cálculo
            </button>
            {showGradeDetails && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>Notas por disciplina</h4>
                {(resource.course_subjects || []).map((s, idx) => (
                  <div key={idx} style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px', color: '#4B5563' }}>{s.serie || `Série ${idx + 1}`}</div>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                          <th style={{ textAlign: 'left', padding: '8px' }}>Disciplina</th>
                          <th style={{ textAlign: 'right', padding: '8px' }}>Peso</th>
                          <th style={{ textAlign: 'right', padding: '8px' }}>Nota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(s.disciplinas || []).map((d, i) => {
                          const nota = resource.analysis_grades?.[s.serie]?.[d.name];
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <td style={{ padding: '8px' }}>{d.name}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{d.weight}</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>{nota != null && nota !== '' ? Number(nota).toFixed(2).replace('.', ',') : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
                {(!resource.course_subjects || resource.course_subjects.length === 0) && resource.analysis_grades && (
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    {Object.entries(resource.analysis_grades).map(([serie, disc]) => (
                      <div key={serie} style={{ marginBottom: '8px' }}>
                        <strong>{serie}:</strong> {typeof disc === 'object' ? Object.entries(disc || {}).map(([k, v]) => `${k}: ${v}`).join(', ') : String(disc)}
                      </div>
                    ))}
                  </div>
                )}
                {resource.final_score != null && !isNaN(resource.final_score) && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#ECFDF5', borderRadius: '6px', border: '1px solid #A7F3D0' }}>
                    <strong style={{ color: '#065F46' }}>Média final:</strong> {Number(resource.final_score).toFixed(2).replace('.', ',')}
                  </div>
                )}
                {resource.course_subjects?.length > 0 && resource.analysis_grades && memorySteps.length > 0 && (
                  <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#FFFBEB', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#92400E' }}>Memória de cálculo</h4>
                    {memorySteps.map((step, idx) => (
                      <div key={idx} style={{ marginBottom: '12px', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.6', color: '#374151' }}>
                        <strong style={{ color: '#92400E' }}>{step.serie}:</strong>
                        <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                          <div>{step.formula}</div>
                          {step.intermediario && <div style={{ color: '#6B7280' }}>→ {step.intermediario}</div>}
                          <div style={{ fontWeight: '600', color: '#065F46' }}>→ {step.resultado}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>📝 Narrativa do Candidato</h4>
          <div style={{
            padding: '16px',
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#374151',
          }}
          >
            {resource.narrative || resource.justification || '(não informada)'}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>📎 Documentos do Recurso</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(resource.documents || []).length > 0 ? (
              (resource.documents || []).map((doc, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <span>{doc.name || doc.url || `Documento ${index + 1}`}</span>
                  <button
                    type="button"
                    onClick={() => handleDownloadDocuments(resource, index)}
                    style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                  >
                    📄 Download
                  </button>
                </div>
              ))
            ) : (
              <p style={{ color: '#6B7280', fontStyle: 'italic' }}>Nenhum documento anexado</p>
            )}
          </div>
        </div>

        {error && (
          <div id="appeal-analysis-save-error">
            <Alert type="error" message={error} onClose={() => setError('')} />
          </div>
        )}

        {applicationForCurricular && isListAppeal() && !isOrdemResource ? (
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>📝 Análise Curricular – Lançar notas</h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>Preencha as notas das disciplinas para deferir a inscrição.</p>
            {(() => {
              const courseId = parseInt(applicationForCurricular.course_id || resource.course_id, 10);
              const course = courses.find((c) => c.id === courseId);
              if (!course?.subjects) {
                return (
                  <div style={{ padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px', color: '#92400E' }}>
                    Disciplinas não configuradas para este curso.
                  </div>
                );
              }
              return (
                <>
                  {course.subjects.map((subject, idx) => (
                    <div key={idx} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#FEF7F0', borderRadius: '8px', border: '1px solid #FED7AA' }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#EA580C' }}>📖 {subject.serie}</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                        {(subject.disciplinas || []).map((d, i) => (
                          <div key={i} style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #FED7AA' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#9A3412', marginBottom: '4px' }}>
                              {d.name} (Peso: {d.weight})
                            </label>
                            <GradeInput
                              value={curriculumForm.grades[subject.serie]?.[d.name]}
                              onChange={(v) => handleGradeChange(subject.serie, d.name, v)}
                              style={styles.gradeInput}
                              placeholder="0,00"
                              max={100}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div style={{ padding: '16px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#166534' }}>🧮 Média Ponderada</span>
                      <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#166534' }}>
                        {calculateWeightedAverage(curriculumForm.grades, course.subjects).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
            <div style={styles.formActions}>
              <button type="button" onClick={() => setApplicationForCurricular(null)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Voltar
              </button>
              <button
                type="button"
                onClick={saveListAppealCurricular}
                disabled={(() => {
                  const courseId = parseInt(applicationForCurricular.course_id || resource.course_id, 10);
                  const course = courses.find((c) => c.id === courseId);
                  return !course?.subjects || !validateAllGrades(curriculumForm.grades, course.subjects).isValid;
                })()}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                💾 Salvar e Deferir Inscrição
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>⚖️ Decisão do Recurso</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>
                  📊 Situação <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  value={resourceForm.decision}
                  onChange={(e) => {
                    const newDecision = e.target.value;
                    setResourceForm({
                      ...resourceForm,
                      decision: newDecision,
                      rejection_reason: newDecision === 'approved' ? '' : resourceForm.rejection_reason,
                    });
                  }}
                  style={styles.select}
                >
                  <option value="">Selecione uma situação...</option>
                  <option value="approved">✅ Aprovado</option>
                  <option value="rejected">❌ Rejeitado</option>
                </select>
              </div>
              {isListAppeal() && !applicationForCurricular && !isOrdemResource && (
                <div style={{ marginBottom: '16px' }}>
                  <button type="button" onClick={openCurricularForm} style={{ ...styles.button, ...styles.buttonPrimary }}>
                    📝 Abrir análise curricular
                  </button>
                </div>
              )}
              {resourceForm.decision === 'rejected' && !isAnswerKeyAppeal() && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={styles.label}>❌ Motivo de Rejeição (opcional se preencher observações)</label>
                  <select
                    value={resourceForm.rejection_reason}
                    onChange={(e) => setResourceForm({ ...resourceForm, rejection_reason: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">Selecione um motivo...</option>
                    {filteredResourceReasons.map((reason) => (
                      <option key={reason.id} value={reason.id}>{reason.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={styles.label}>
                  📝 Fundamentação da decisão {resourceForm.decision === 'rejected' ? <span style={{ color: '#EF4444' }}>*</span> : '(opcional para deferido)'}
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
                  Este texto é o que poderá ser exibido ao candidato quando o processo permitir e o administrador publicar a resposta em &quot;Ver recurso&quot;.
                </div>
                <textarea
                  value={resourceForm.notes}
                  onChange={(e) => setResourceForm({ ...resourceForm, notes: e.target.value })}
                  style={styles.textarea}
                  placeholder="Fundamentação da decisão sobre o recurso..."
                  rows="4"
                />
              </div>
            </div>
            <div style={styles.formActions}>
              {error && (
                <div id="appeal-analysis-save-error-bottom" style={{ flex: '1 1 100%', marginBottom: 8 }}>
                  <Alert type="error" message={error} onClose={() => setError('')} />
                </div>
              )}
              <button type="button" onClick={handleClose} disabled={saving} style={{ ...styles.button, ...styles.buttonSecondary }}>
                ❌ Cancelar
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  if (!resourceForm.decision) { reportSaveError('Selecione uma situação'); return; }
                  if (
                    resourceForm.decision === 'rejected'
                    && !isAnswerKeyAppeal()
                    && !resourceForm.rejection_reason
                    && !resourceForm.notes
                  ) {
                    reportSaveError('Informe motivo ou observações ao rejeitar');
                    return;
                  }
                  if (
                    resourceForm.decision === 'rejected'
                    && isAnswerKeyAppeal()
                    && !(resourceForm.notes || '').trim()
                  ) {
                    reportSaveError('Informe a fundamentação da decisão ao indeferir');
                    return;
                  }
                  await saveResourceAnalysis();
                }}
                style={{ ...styles.button, ...styles.buttonPrimary, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Salvando…' : '💾 Salvar'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default AppealAnalysisModal;
